import { useEffect, useRef } from 'react'

interface WaveformProps {
  isRecording: boolean
  audioStream: MediaStream | null
  className?: string
}

function readCssVar(name: string, fallback: string): string {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return fallback
  }
  const value = window.getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return value || fallback
}

type WaveformColors = {
  backgroundColor: string
  gridColor: string
  waveformColor: string
}

function resolveWaveformColors(): WaveformColors {
  const backgroundColor = readCssVar('--color-navy', '#0a1929')
  const gridColor = readCssVar('--color-navy-light', '#132f4c')
  const waveformColor = readCssVar('--color-neon-cyan', '#00e5ff')
  return { backgroundColor, gridColor, waveformColor }
}

function drawBackground(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, colors: WaveformColors): void {
  ctx.fillStyle = colors.backgroundColor
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  // Draw subtle grid pattern
  ctx.strokeStyle = colors.gridColor
  ctx.lineWidth = 1
  const gridSize = 20
  for (let x = 0; x <= canvas.width; x += gridSize) {
    ctx.beginPath()
    ctx.moveTo(x, 0)
    ctx.lineTo(x, canvas.height)
    ctx.stroke()
  }
  for (let y = 0; y <= canvas.height; y += gridSize) {
    ctx.beginPath()
    ctx.moveTo(0, y)
    ctx.lineTo(canvas.width, y)
    ctx.stroke()
  }
}

export function Waveform({ isRecording, audioStream, className = '' }: WaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationFrameRef = useRef<number | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const colorsRef = useRef<WaveformColors>(resolveWaveformColors())

  useEffect(() => {
    // Ensure we have up-to-date colors when starting/stopping recording.
    colorsRef.current = resolveWaveformColors()

    if (!isRecording || !audioStream) {
      // Clear canvas when not recording
      const canvas = canvasRef.current
      if (canvas) {
        const ctx = canvas.getContext('2d')
        if (ctx) {
          drawBackground(canvas, ctx, colorsRef.current)
        }
      }
      return
    }

    // Setup audio context and analyser
    const audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)()
    const analyser = audioContext.createAnalyser()
    const source = audioContext.createMediaStreamSource(audioStream)

    analyser.fftSize = 256
    analyser.smoothingTimeConstant = 0.8
    source.connect(analyser)

    audioContextRef.current = audioContext
    analyserRef.current = analyser

    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const bufferLength = analyser.frequencyBinCount
    const dataArray = new Uint8Array(bufferLength)

    const draw = () => {
      if (!isRecording || !analyserRef.current) {
        return
      }

      animationFrameRef.current = requestAnimationFrame(draw)

      analyserRef.current.getByteFrequencyData(dataArray)

      // Clear canvas with grid background
      drawBackground(canvas, ctx, colorsRef.current)

      // Draw waveform line (bright blue, smooth curve)
      ctx.strokeStyle = colorsRef.current.waveformColor
      ctx.lineWidth = 3
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.beginPath()

      const sliceWidth = canvas.width / bufferLength
      let x = 0

      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 255.0
        const y = (v * canvas.height) / 2

        if (i === 0) {
          ctx.moveTo(x, canvas.height / 2 - y)
        } else {
          ctx.lineTo(x, canvas.height / 2 - y)
        }

        x += sliceWidth
      }

      // Mirror the waveform for symmetry
      x = 0
      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 255.0
        const y = (v * canvas.height) / 2

        if (i === 0) {
          ctx.moveTo(x, canvas.height / 2 + y)
        } else {
          ctx.lineTo(x, canvas.height / 2 + y)
        }

        x += sliceWidth
      }

      ctx.stroke()
    }

    // Resume audio context on user interaction (required by browsers)
    audioContext.resume().then(() => {
      draw()
    })

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
      if (audioContextRef.current) {
        audioContextRef.current.close()
      }
    }
  }, [isRecording, audioStream])

  return (
    <div className={`relative w-full ${className}`} role="region" aria-label="Audio waveform visualization">
      <canvas
        ref={canvasRef}
        width={400}
        height={200}
        className="w-full h-auto rounded-xl bg-navy"
        aria-label={isRecording ? 'Live audio waveform visualization' : 'Waveform display'}
        aria-live={isRecording ? 'polite' : 'off'}
        aria-atomic="true"
      />
      {!isRecording && !audioStream && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-navy-dark/30 rounded-xl"
          role="status"
          aria-label="Waveform ready - waiting for recording to start"
        >
          <p className="text-gray-400 text-sm">Waveform will appear when recording starts</p>
        </div>
      )}
      {isRecording && audioStream && (
        <div className="sr-only" role="status" aria-live="polite">
          Recording in progress - waveform displaying audio levels
        </div>
      )}
    </div>
  )
}
